import { Link, NavLink, Outlet } from 'react-router-dom'
import { LayoutDashboard, User, MessageSquare, Calendar, PlusCircle, Receipt } from 'lucide-react'
import { Container } from './Container'
import { PageHero } from './PageHero'
import { RouteSeoFallback } from '@/components/RouteSeoFallback'
import { cn } from '@/lib/utils'
import { PATHS } from '@/routes/paths'
import { useAuthStore } from '@/store/authStore'
import { trackPostPropertyClick } from '@/lib/analytics/sellerEvents'

const navItems = [
  { to: PATHS.dashboard, label: 'Dashboard', icon: LayoutDashboard },
  { to: PATHS.editProfile, label: 'Profile', icon: User },
  { to: PATHS.myOrders, label: 'Orders & Payments', icon: Receipt },
  { to: PATHS.myInquiry, label: 'Inquiries', icon: MessageSquare },
  { to: PATHS.mySiteVisit, label: 'Visits', icon: Calendar },
]

export function DashboardLayout() {
  const { isAuthenticated } = useAuthStore()

  return (
    <>
      <RouteSeoFallback />
      <PageHero
        title="My Account"
        subtitle="Manage your properties, inquiries, and site visits"
        crumbs={[{ label: 'Home', href: PATHS.home }, { label: 'Account' }]}
        compact
      />
      <Container className="py-4 md:py-8">
        {isAuthenticated && (
          <nav className="mb-4 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1 hide-scrollbar lg:hidden">
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    'flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold transition-colors',
                    isActive
                      ? 'bg-primary-600 text-white shadow-sm shadow-primary-900/20'
                      : 'bg-white text-neutral-600 ring-1 ring-neutral-200 hover:bg-neutral-50',
                  )
                }
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </NavLink>
            ))}
          </nav>
        )}

        <div
          className={cn(
            'grid gap-5 lg:gap-6',
            isAuthenticated && 'lg:grid-cols-[250px_1fr]',
          )}
        >
          {isAuthenticated && (
            <aside className="hidden lg:block">
              <nav className="sticky top-28 space-y-1.5 rounded-2xl border border-neutral-200 bg-white p-3 shadow-sm">
                {navItems.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-200'
                          : 'text-neutral-600 hover:bg-neutral-50',
                      )
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </NavLink>
                ))}
                <Link
                  to={PATHS.addProperty}
                  onClick={() => trackPostPropertyClick('dashboard_sidebar')}
                  className="mt-2 flex items-center gap-3 rounded-xl bg-primary-500 px-3 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary-900/25"
                >
                  <PlusCircle className="h-4 w-4" /> Post Property
                </Link>
              </nav>
            </aside>
          )}
          <div className="min-w-0 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm md:p-5 lg:p-6">
            <Outlet />
          </div>
        </div>
      </Container>
    </>
  )
}
